// Considered: no existing aspect viewer — schema property tables are schema-shaped, not
// aspect-shaped (`AspectDefinition.properties` reuses `SchemaProperty` but the surrounding
// family/metric/value-policy summary has no aspect-domain home anywhere else). Built fresh
// from `AspectDefinition`'s own shape. See spec Design System Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { AspectDefinition } from 'types/aspect';
import TagAtom from '../../atoms/Tag/TagAtom';
import { StatusBadgeAtom } from '../../atoms/StatusBadge';
import './AspectSummaryMolecule.scss';

export interface AspectSummaryMoleculeProps {
  aspect: AspectDefinition;
  className?: string;
}

const AspectSummaryMolecule: React.FC<AspectSummaryMoleculeProps> = ({
  aspect,
  className = '',
}) => {
  const { t } = useTranslation();
  const rootClasses = ['aspect-summary', className].filter(Boolean).join(' ');
  const properties = aspect.properties ?? [];

  return (
    <div className={rootClasses} data-testid="aspect-summary">
      {(aspect.family || aspect.metric) && (
        <div className="aspect-summary__meta">
          {aspect.family && (
            <TagAtom tone="outline" icon="pi-sitemap" label={aspect.family} />
          )}
          {aspect.metric && (
            <StatusBadgeAtom
              tone="info"
              label={t('studio.gallery.drawer.aspect.metricBadge')}
              data-testid="aspect-summary-metric-badge"
            />
          )}
        </div>
      )}

      {aspect.valuePolicy && (
        <p className="aspect-summary__value-policy">
          {t('studio.gallery.drawer.aspect.valuePolicyLine', {
            temporal: t(`studio.gallery.drawer.aspect.temporal.${aspect.valuePolicy.temporal}`),
            attribution: t(
              `studio.gallery.drawer.aspect.attribution.${aspect.valuePolicy.attribution}`,
            ),
          })}
        </p>
      )}

      {properties.length === 0 ? (
        <p className="aspect-summary__empty">{t('studio.gallery.drawer.aspect.noProperties')}</p>
      ) : (
        <table className="aspect-summary__table">
          <thead>
            <tr>
              <th scope="col">{t('studio.gallery.drawer.aspect.columnName')}</th>
              <th scope="col">{t('studio.gallery.drawer.aspect.columnType')}</th>
              <th scope="col">{t('studio.gallery.drawer.aspect.columnRequired')}</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((prop) => (
              <tr key={prop.name} data-testid={`aspect-summary-property-${prop.name}`}>
                <td>{prop.label || prop.name}</td>
                <td>{prop.type}</td>
                <td>
                  {prop.required
                    ? t('studio.gallery.drawer.aspect.requiredYes')
                    : t('studio.gallery.drawer.aspect.requiredNo')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

AspectSummaryMolecule.displayName = 'AspectSummaryMolecule';

export default AspectSummaryMolecule;
